import { useEffect } from 'react'
import { Link, useParams, useSearchParams, useNavigate, useOutletContext } from 'react-router-dom'
import { useComputed, useSignal, useSignalEffect } from '@preact/signals-react'

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Stack from 'react-bootstrap/Stack'

import ConfirmationModal from '../../components/ConfirmationModal'
import DebugAccordion from '../../components/DebugAccordion'
import JsonForm from '../../components/JsonForm.jsx'
import Loadable from '../../components/Loadable.jsx'
import PageTitle from '../../components/PageTitle.jsx'
import ConfigTemplateNav, { ConfigTemplateSubNav } from './components/ConfigTemplateNav'
import ConfigTemplateDeviceChecklist from './components/ConfigTemplateDeviceChecklist'

import { NotificationTypes, addNotification } from '../../state/actions/notificationActions'
import { fetchConfigTemplate, updateConfigTemplate } from '../../state/actions/configTemplateActions'
import { fetchDeviceSchemas } from '../../state/actions/deviceSchemaActions'

import { configTemplateSignal, configTemplateLoadedSignal, configTemplateErrorSignal } from '../../state/signals/configTemplateSignals'
import { deviceSchemasSignal, deviceSchemasLoadedSignal, deviceSchemasErrorSignal } from '../../state/signals/deviceSchemaSignals'

import { buildDeviceCheckedSetFromConfig, buildFilteredDeviceSchema, withDefaultCheckedDeviceFields } from './configTemplateSchemaUtils'
import { checkDeviceDescriptionConflicts, isEmptyObject, makeFieldsReadonly } from '../../utils/utils'
import useRedirectOnBeIdChange from '../../hooks/useRedirectOnBeIdChange.js'

export default function ConfigTemplateDeviceDetail() {

    const navigate = useNavigate()
    const { beId, configTemplateId, name } = useParams()
    const beIdChanged = useRedirectOnBeIdChange(beId, `/be/${beId}/templates`)
    const [searchParams] = useSearchParams()
    const debug = !!searchParams.get('debug')
    const desc = searchParams.get('desc')
    // presence of ?desc= means we are editing an existing device, even if the name is blank
    const editMode = desc !== null
    const { config, accessToken, userPermissionsSummary } = useOutletContext()

    useEffect(() => {
        if (userPermissionsSummary.value && !userPermissionsSummary.value.canViewConfigTemplates) {
            addNotification('You are not authorized to view config templates for this account.', NotificationTypes.ERROR, true)
            navigate('/')
        }
    }, [userPermissionsSummary.value, navigate])

    const canEditComputed = useComputed(() => userPermissionsSummary?.value?.canEditConfigTemplates)

    useEffect(() => {
        if (beIdChanged) return
        fetchConfigTemplate(beId, configTemplateId, config, accessToken)
        fetchDeviceSchemas(config, accessToken)
    }, [beId, beIdChanged, configTemplateId, config, accessToken])

    const parsedConfigComputed = useComputed(() => {
        try { return JSON.parse(configTemplateSignal.value?.config || '{}') } catch { return {} }
    })

    const deviceIndexComputed = useComputed(() => {
        if (!configTemplateLoadedSignal.value || !editMode) return -1
        const devices = parsedConfigComputed.value?.deviceConfig?.device ?? []
        return devices.findIndex(a => a.deviceType == name && a.taskDesc == desc)
    })

    const deviceComputed = useComputed(() => {
        if (!configTemplateLoadedSignal.value || !editMode) return null
        const index = deviceIndexComputed.value
        if (index < 0) return null
        const current = parsedConfigComputed.value.deviceConfig.device[index]
        return { config: current }
    })

    const deviceSchemaComputed = useComputed(() => {
        if (!deviceSchemasSignal.value?.length) return {}
        const schema = deviceSchemasSignal.value.find(a => a.name == name)
        return schema?.schemaValue ?? {}
    })

    // The checklist renders a section per entry; on this page we scope it to the
    // single device type being edited.
    const currentDeviceSchemaListComputed = useComputed(() => {
        if (!deviceSchemasSignal.value?.length) return []
        const schema = deviceSchemasSignal.value.find(a => a.name == name)
        return schema ? [schema] : []
    })

    // Field selection for this device type only. Seeded from the device already stored
    // on the template so an existing device opens with its own fields checked, plus the
    // fields that are always checked by default (Device Name).
    const checkedFieldsSignal = useSignal(new Set())

    useSignalEffect(() => {
        if (configTemplateLoadedSignal.value && deviceSchemasLoadedSignal.value) {
            const stored = deviceComputed.value?.config
            const seeded = stored ? buildDeviceCheckedSetFromConfig([stored]) : new Set()
            checkedFieldsSignal.value = withDefaultCheckedDeviceFields(name, seeded)
        }
    })

    const showCancelModalSignal = useSignal(false)
    const isFormEditedSignal = useSignal(false)
    const isFormSubmittingSignal = useSignal(false)

    const handleFormSubmit = values => {
        if (isFormSubmittingSignal.value) return false
        isFormSubmittingSignal.value = true
        try {
            if (values) {
                const parsed = parsedConfigComputed.value
                const updatedConfig = structuredClone(parsed)
                updatedConfig.deviceConfig = updatedConfig.deviceConfig || {}
                updatedConfig.deviceConfig.device = updatedConfig.deviceConfig.device || []

                // index is -1 when adding, or when ?desc= names a device the template
                // no longer has; either way this is an insert rather than a replace
                const index = deviceIndexComputed.value
                if (editMode && index >= 0) {
                    updatedConfig.deviceConfig.device[index] = values
                } else {
                    updatedConfig.deviceConfig.device.push(values)
                }

                if (checkDeviceDescriptionConflicts(updatedConfig.deviceConfig.device)) {
                    addNotification('Unable to save due to conflict. All Devices must have a unique Device Name.', NotificationTypes.ERROR)
                    isFormSubmittingSignal.value = false
                    return false
                }

                updateConfigTemplate(beId, { ...configTemplateSignal.value, config: JSON.stringify(updatedConfig) }, config, accessToken)
                    .then(result => {
                        configTemplateSignal.value = result
                        navigate(`/be/${beId}/templates/${configTemplateId}/devices`)
                        addNotification('Device was successfully updated.', NotificationTypes.SUCCESS, true)
                    })
                    .catch(error => {
                        addNotification(`Unable to update the Device. ${error.toString()}`, NotificationTypes.ERROR, false)
                    })
                    .finally(() => {
                        isFormSubmittingSignal.value = false
                    })
            }
        } catch (error) {
            addNotification(`Unable to update the Device. ${error.toString()}`, NotificationTypes.ERROR, false)
            isFormSubmittingSignal.value = false
        }
        return false
    }

    const handleCancel = () => {
        if (isFormEditedSignal.value) {
            showCancelModalSignal.value = true
        } else {
            navigate('../')
        }
    }

    const handleFormChange = () => {
        isFormEditedSignal.value = true
    }

    const jsonFormDataComputed = useComputed(() => {
        if (!deviceSchemasLoadedSignal.value || !configTemplateLoadedSignal.value) return null
        if (isEmptyObject(deviceSchemaComputed.value) || isEmptyObject(configTemplateSignal.value)) return null
        if (checkedFieldsSignal.value.size === 0) return null

        // Restrict the form to the fields checked on the left, the same way the
        // config tab filters the template's schema.
        const filteredSchema = buildFilteredDeviceSchema(name, deviceSchemaComputed.value, checkedFieldsSignal.value)
        if (!filteredSchema) return null

        let clonedSchema = structuredClone(filteredSchema)
        if (!canEditComputed.value) clonedSchema = makeFieldsReadonly(clonedSchema)

        globalThis.SCHEMA_MODE = 'CONFIG'

        const formData = {
            schema: clonedSchema.properties.config,
            loadedComponentConfig: parsedConfigComputed.value,
            onSubmitValid: handleFormSubmit,
            onCancel: handleCancel,
            onFormChange: handleFormChange,
            canEditConfig: canEditComputed.value
        }
        if (deviceComputed.value) {
            formData.value = deviceComputed.value.config
        }
        return formData
    })

    const accordionData = [
        { name: 'Device', data: deviceComputed.value },
        { name: 'Device Index', data: deviceIndexComputed.value },
        { name: 'Device Schema', data: deviceSchemaComputed.value },
        { name: 'Config Template', data: configTemplateSignal.value },
        { name: 'Access Token', data: accessToken },
        { name: 'App Config', data: config }
    ]

    return (
        <>
            <ConfirmationModal
                show={showCancelModalSignal.value}
                handleCancel={() => showCancelModalSignal.value = false}
                handleConfirm={() => navigate('../devices')}
                message="You have unsaved changes to this form. Are you sure you want to leave and lose these changes?"
                title="Warning: Unsaved Changes"
                cancelText="Continue Editing"
                confirmText="Lose Changes"
            />
            <Breadcrumb className="px-3 pt-1 bg-light">
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/templates` }}>
                    Config Template List
                </Breadcrumb.Item>
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/templates/${configTemplateId}/devices` }}>
                    {configTemplateLoadedSignal.value ? <>Manage {configTemplateSignal.value?.name}</> : <>Loading...</>}
                </Breadcrumb.Item>
                <Breadcrumb.Item active>
                    {editMode ? `Manage ${name} Device` : `Add ${name} Device`}
                </Breadcrumb.Item>
            </Breadcrumb>
            <Stack direction="horizontal" gap={3}>
                <PageTitle title={editMode ? `Manage ${name} Device` : `Add ${name} Device`} canEditSignal={canEditComputed} />
            </Stack>
            <ConfigTemplateNav activeKey="2" />
            <ConfigTemplateSubNav activeKey="4" />
            <Loadable loadedSignals={[configTemplateLoadedSignal, deviceSchemasLoadedSignal]} errorSignals={[configTemplateErrorSignal, deviceSchemasErrorSignal]}>
                <div className="d-flex gap-3 px-3">
                    <div style={{ width: '300px', flexShrink: 0, overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
                        <ConfigTemplateDeviceChecklist
                            deviceSchemas={currentDeviceSchemaListComputed.value}
                            checkedFieldsSignal={checkedFieldsSignal}
                        />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        {checkedFieldsSignal.value.size === 0 ? (
                            <p className="text-muted mt-3">Select device fields on the left to include them in this config template.</p>
                        ) : (
                            <JsonForm data={jsonFormDataComputed.value} />
                        )}
                    </div>
                </div>
            </Loadable>
            <DebugAccordion data={accordionData} debug={debug} />
        </>
    )
}
